import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import { AdminAPI, getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import * as Dialog from '@radix-ui/react-dialog'
import { Trans, useLingui } from '@lingui/react/macro'

import Field from '@/components/ui/Field'
import { QrCodeContent } from '../hooks/useQrCode'
import { ArrowDown01Icon } from '@hugeicons/core-stroke-rounded'
import { HugeiconsIcon } from '@hugeicons/react'
import { dialogContainer, DialogHeader } from '@/layouts/Dialog'
import { SelectGoodie } from '@/features/goodies'
import Button from '@/components/ui/Button'

export function GoodieUrl({
  appUrl,
  onChange
}: {
  appUrl: string
  onChange: (urls: QrCodeContent[]) => void
}) {
  const { t } = useLingui()
  const [open, setOpen] = useState(false)
  const [goodieId, setGoodieId] = useState<number | null>(null)

  const { data } = useQuery(AdminAPI.goodie.GET, {
    variables: { id: goodieId ?? -1 },
    skip: !goodieId
  })

  const goodie = useMemo(
    () => getFragment(SharedAPI.goodie.GOODIE_FRAGMENT, data?.getGoodie),
    [data]
  )

  useEffect(() => {
    if (!goodie) return

    onChange([
      {
        text: goodie.title,
        url: `${appUrl}/goodies/${goodie.token}`
      }
    ])
  }, [appUrl, goodie, onChange])

  const onGoodieSelected = useCallback(
    (goodie: GraphQL.GoodieFragmentFragment) => {
      setGoodieId(goodie.id)
      setOpen(false)
    },
    [setGoodieId, setOpen]
  )

  return (
    <div>
      <Field label={t`Choose Goodie`} size="small">
        <Dialog.Root open={open} onOpenChange={setOpen}>
          <Dialog.Trigger>
            <button
              className="w-full flex select-none flex-row items-center gap-1 rounded-small border border-text bg-white py-1.5 pl-2 pr-3"
              tabIndex={0}
              type="button"
            >
              <HugeiconsIcon icon={ArrowDown01Icon} className="size-4" />
              <span className="text-text">
                {goodie ? (
                  <>
                    <span>{goodie?.name}</span>
                  </>
                ) : (
                  <Trans>Choose Goodie</Trans>
                )}
              </span>
            </button>
          </Dialog.Trigger>
          <Dialog.Portal container={dialogContainer}>
            <Dialog.Content
              className="absolute left-1/2 top-1/2 h-screen w-screen -translate-x-1/2 -translate-y-1/2 overflow-y-scroll py-20"
              onPointerDownOutside={() => setOpen(false)}
            >
              <div className="pointer-events-auto relative mx-auto w-screen max-w-[800px] overflow-hidden rounded bg-white shadow-md flex flex-col gap-2">
                <div className="p-4">
                  <DialogHeader
                    title={t`Choose Goodie`}
                    description={t`Choose a goodie for the QR Code that links to the chosen Goodie.`}
                    close={() => setOpen(false)}
                  />
                </div>
                <div className="flex flex-col p-8">
                  <SelectGoodie onSelectedGoodie={onGoodieSelected} />
                  <div className="flex flex-row justify-start">
                    <Button variant="outline" onClick={() => setOpen(false)}>
                      <Trans>Cancel</Trans>
                    </Button>
                  </div>
                </div>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </Field>
    </div>
  )
}
